<template>
  <AppNavbar />
  <div class="container mt-5">
    <AppHeader title="Gestión de Facturas">
      <template #buttons>
        <router-link class="btn btn-success me-2" :to="{ name: 'RegistrarFactura' }">Registrar Factura</router-link>
      </template>
    </AppHeader>


    <!-- pos-graciela-api/docs/SPEC_LISTADO_COMPRAS_v0.1.md: búsqueda y período
         se resuelven en el API (RL-1, RL-5); acá sólo se arma el rango. -->
    <div class="row g-2 align-items-center mt-4 mb-3">
      <div class="col-12 col-md-5">
        <input
          v-model="searchInput"
          type="search"
          class="form-control"
          placeholder="Buscar por número de comprobante o proveedor..."
          data-testid="buscar-compras"
          @input="buscarConDemora"
        />
      </div>
      <div class="col-12 col-md-7 d-flex flex-wrap align-items-center gap-2">
        <select v-model="periodo" class="form-select w-auto" aria-label="Período" data-testid="periodo" @change="cambiarPeriodo">
          <option value="todas">Todas las fechas</option>
          <option value="dia">Día</option>
          <option value="semana">Semana</option>
          <option value="mes">Mes</option>
        </select>
        <template v-if="periodo !== 'todas'">
          <button type="button" class="btn btn-outline-secondary" aria-label="Período anterior" data-testid="periodo-anterior" @click="moverPeriodo(-1)">‹</button>
          <input v-model="fechaReferencia" type="date" class="form-control w-auto" aria-label="Fecha" data-testid="fecha-periodo" @change="aplicarFiltros" />
          <button type="button" class="btn btn-outline-secondary" aria-label="Período siguiente" data-testid="periodo-siguiente" @click="moverPeriodo(1)">›</button>
          <small v-if="periodo !== 'dia'" class="text-muted" data-testid="rango-periodo">
            del {{ fechaCorta(rango.desde) }} al {{ fechaCorta(rango.hasta) }}
          </small>
        </template>
      </div>
    </div>


    <!-- Tabla de Comprobantes -->
    <div class="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
      <h2>Lista de Facturas</h2>
      <small class="text-muted" data-testid="resumen-paginacion">
        Página {{ paginaActual }} de {{ Math.max(totalPaginas, 1) }} · {{ totalRegistros }} {{ totalRegistros === 1 ? 'factura' : 'facturas' }}
      </small>
    </div>
    <div class="table-responsive">
    <table class="table table-striped">
      <thead>
        <tr>
          <th>N° de Comprobante</th>
          <th>Proveedor</th>
          <th>Fecha</th>
          <th>Monto Total</th>
          <th>Estado</th>
          <th>Tipo de Comprobante</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!cargando && comprobantes.length === 0">
          <td colspan="7" class="text-center text-muted" data-testid="sin-compras">No hay facturas para este filtro.</td>
        </tr>
        <tr v-for="comprobante in comprobantes" :key="comprobante.id_compra" :data-id-compra="comprobante.id_compra">
          <td>{{ comprobante.nro_comprobante || 'N/A' }}</td>
          <td>{{ comprobante.nombre_razon_social || 'N/A' }}</td>
          <td>{{ formatearFecha(comprobante.fecha_emision) }}</td>
          <td>{{ formatearMonto(Math.trunc(comprobante.total_iva_incluido) || Math.trunc(comprobante.total_sin_iva)) || 'N/A' }}</td>
          <td>{{ comprobante.estado }}</td>

          <td>
            <!-- SPEC_CARGA_INICIAL_STOCK_Y_PRECIOS_v0.1.md RC-5 / CA-20 -->
            <span v-if="comprobante.origen === 'CARGA_INICIAL'" class="badge bg-info-subtle text-info-emphasis" data-testid="etiqueta-carga-inicial">Carga inicial</span>
            <span v-else>Factura</span>
          </td>
          <td>
            <button class="btn btn-primary btn-sm me-1" @click="verDetalleComprobante(comprobante)">Ver</button>
            <button
              v-if="comprobante.origen === 'CARGA_INICIAL' && cargaInicialHabilitada"
              class="btn btn-outline-primary btn-sm me-1"
              :data-testid="`abrir-carga-${comprobante.nro_comprobante}`"
              @click="$router.push({ name: 'EditarCargaInicialPorId', params: { idCompra: comprobante.id_compra } })"
            >
              Editar carga
            </button>
            <button class="btn btn-danger btn-sm me-1" :disabled="comprobante.estado === 'anulado'" @click="anularComprobante(comprobante)">Anular</button>
          </td>
        </tr>
      </tbody>
    </table>
    </div>


    <!-- Controles de Paginación -->
    <AppPagination :currentPage="paginaActual" :totalPages="totalPaginas" @page-changed="cambiarPagina" />
  </div>
</template>

<script>
import ApiServices from '../services/apiService.js';
import AppNavbar from '../components/AppNavbar.vue';
import AppHeader from '../components/AppHeader.vue';
import AppPagination from '../components/AppPagination.vue';
import operationalConfigService from '../services/operationalConfigService';
import { rutaApiCompra } from '@/utils/compraRef.mjs';
import { rangoDePeriodo, moverPeriodo, hoyISO, fechaCorta } from '@/utils/periodoFechas.mjs';

const POR_PAGINA = 10;

export default {
  name: 'ListarComprobantes',
  components: {
    AppNavbar,
    AppHeader,
    AppPagination
  },
  data() {
    return {
      cargaInicialHabilitada: operationalConfigService.getConfig().carga_inicial_stock_habilitada === true,
      comprobantes: [],
      searchInput: '',
      periodo: 'todas',
      fechaReferencia: hoyISO(),
      paginaActual: 1,
      totalPaginas: 1,
      totalRegistros: 0,
      cargando: false,
      pedidoVigente: 0,
      demoraBusqueda: null
    };
  },
  computed: {
    rango() {
      return rangoDePeriodo(this.periodo, this.fechaReferencia);
    }
  },
  methods: {
    fechaCorta,
    // SPEC_LISTADO_COMPRAS_v0.1.md RL-1: cada página se pide al API, que
    // ordena (RL-2) y filtra (RL-3, RL-5). Una respuesta vieja (el operador
    // siguió tipeando o cambió de página) se descarta.
    async cargarComprobantes(page = this.paginaActual) {
      const pedido = ++this.pedidoVigente;
      this.cargando = true;
      try {
        const params = { page, limit: POR_PAGINA };
        const search = this.searchInput.trim();
        if (search) params.search = search;
        if (this.rango.desde) params.fecha_desde = this.rango.desde;
        if (this.rango.hasta) params.fecha_hasta = this.rango.hasta;

        const response = await ApiServices.get('/api/purchases/invoices', params);
        if (pedido !== this.pedidoVigente) return;
        const facturas = response.data.data || [];
        const pagination = response.data.pagination || {};
        const totalPaginas = Number(pagination.totalPages) || 0;

        // Tras anular la última compra de la última página, ir a la anterior.
        if (facturas.length === 0 && page > 1 && totalPaginas > 0) {
          await this.cargarComprobantes(totalPaginas);
          return;
        }

        facturas.forEach(factura => {
          factura.tipo = 'factura';
        });
        this.comprobantes = facturas;
        this.paginaActual = Number(pagination.page) || page;
        this.totalPaginas = totalPaginas;
        this.totalRegistros = Number(pagination.total) || 0;
      } catch (error) {
        if (pedido !== this.pedidoVigente) return;
        console.error('Error al cargar facturas:', error);
        const motivo = error?.response?.status === 400 ? error?.response?.data?.message : null;
        alert.error(motivo || 'No se pudieron cargar las facturas. Intente de nuevo.');
      } finally {
        if (pedido === this.pedidoVigente) this.cargando = false;
      }
    },
    aplicarFiltros() {
      if (this.periodo !== 'todas' && !this.fechaReferencia) this.fechaReferencia = hoyISO();
      this.cargarComprobantes(1);
    },
    buscarConDemora() {
      clearTimeout(this.demoraBusqueda);
      this.demoraBusqueda = setTimeout(() => this.aplicarFiltros(), 300);
    },
    cambiarPeriodo() {
      // Al elegir día, semana o mes se arranca en el de hoy (RL-4).
      this.fechaReferencia = hoyISO();
      this.aplicarFiltros();
    },
    moverPeriodo(paso) {
      this.fechaReferencia = moverPeriodo(this.periodo, this.fechaReferencia || hoyISO(), paso);
      this.aplicarFiltros();
    },
    cambiarPagina(page) {
      if (page === this.paginaActual) return;
      this.cargarComprobantes(page);
    },
    formatearMonto(monto) {
      if (monto === undefined || monto === null) return "0";
      return Number(Math.round(monto)).toLocaleString('es-PY', { minimumFractionDigits: 0 });
    },
    formatearFecha(fecha) {
      if (!fecha) return 'N/A';
      // Convierte de formato ISO (YYYY-MM-DD) o (YYYY-MM-DDTHH:MM:SS) a DD/MM/YYYY
      const fechaObj = new Date(fecha);
      const dia = String(fechaObj.getDate()).padStart(2, '0');
      const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
      const anio = fechaObj.getFullYear();
      return `${dia}/${mes}/${anio}`;
    },
    async anularComprobante(comprobante) {
      const confirmed = await confirm({
        message: '¿Está seguro de que desea anular este comprobante?',
        title: 'Anular comprobante',
        confirmText: 'Anular',
        cancelText: 'Cancelar'
      });
      if (!confirmed) return;

      try {
        // Por id_compra (SPEC_COMPRAS_POR_ID_v0.1.md): el número puede repetirse entre proveedores.
        await ApiServices.delete(
          `${process.env.VUE_APP_API_BASE_URL}${rutaApiCompra({ idCompra: comprobante.id_compra })}`
        );
        comprobante.estado = 'anulado';
  alert.success('Comprobante anulado correctamente.');
        await this.cargarComprobantes();
      } catch (error) {
        console.error('Error al anular comprobante:', error);
        // Un rechazo con motivo (p. ej. «ya tiene pago registrado», 409) se muestra tal cual.
        const motivo = error?.response?.status < 500 ? error?.response?.data?.message : null;
        alert.error(motivo || 'No se pudo anular el comprobante. Por favor, intente de nuevo.');
      }
    },
    verDetalleComprobante(comprobante) {
      console.log("Tipo: factura");
      this.$router.push({ name: 'FacturaPorId', params: { idCompra: comprobante.id_compra } });
    },
  // Funcionalidades de impresión deshabilitadas temporalmente
  },
  async mounted() {
    // La caché local puede estar vieja (el switch se cambia en otra pantalla
    // o en otra PC): se lee del API para mostrar «Editar carga».
    operationalConfigService.loadConfig().then((config) => {
      this.cargaInicialHabilitada = config?.carga_inicial_stock_habilitada === true;
    });
    await this.cargarComprobantes(1);
  },
  beforeUnmount() {
    clearTimeout(this.demoraBusqueda);
  }
};
</script>

<style scoped>
/* Estilos generales */
.container {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
h1, h2 {
  color: #343a40;
}
.form-label {
  font-weight: bold;
  color: #495057;
}
</style>
